import {
  collection,
  query,
  getDocs,
  doc,
  addDoc,
  deleteDoc,
  orderBy,
  where,
  getDoc,
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from '@/lib/firebase/firestore';

export interface BannedWord {
  id: string;
  word: string;
  reason?: string;
  bannedBy: string;
  bannedAt: any;
}

// Check if user is admin (reused from admin-api)
async function checkAdminStatus(): Promise<boolean> {
  const auth = getAuth();
  const currentUser = auth.currentUser;

  if (!currentUser) {
    return false;
  }

  // Check if the user is the main admin
  if (currentUser.uid === 'tOCaPaZ9XDb3eBXgoNYzGPWeyY2') {
    return true;
  }

  // Check if user has isAdmin flag
  const userRef = doc(db, 'users', currentUser.uid);
  const userDoc = await getDoc(userRef);

  if (userDoc.exists() && userDoc.data()?.isAdmin === true) {
    return true;
  }

  return false;
}

// Get all banned words
export async function getAllBannedWords(): Promise<BannedWord[]> {
  try {
    const bannedWordsQuery = query(
      collection(db, 'banned_words'),
      orderBy('bannedAt', 'desc')
    );
    
    const snapshot = await getDocs(bannedWordsQuery);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as BannedWord[];
  } catch (error) {
    console.error('Error fetching banned words:', error);
    return [];
  }
}

// Get banned words as a simple array of strings (for AI prompts)
export async function getBannedWordsArray(): Promise<string[]> {
  try {
    const bannedWords = await getAllBannedWords();
    return bannedWords.map(item => item.word.toLowerCase());
  } catch (error) {
    console.error('Error fetching banned words array:', error);
    return [];
  }
}

// Add a word to banned list
export async function banWord(word: string, reason?: string): Promise<void> {
  try {
    // Check admin status
    const isAdmin = await checkAdminStatus();
    if (!isAdmin) {
      throw new Error('You must be an admin to ban words');
    }

    const auth = getAuth();
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      throw new Error('You must be logged in to ban words');
    }

    // Normalize the word
    const normalizedWord = word.toLowerCase().trim();
    
    // Check if word is already banned
    const existingQuery = query(
      collection(db, 'banned_words'),
      where('word', '==', normalizedWord)
    );
    
    const existingSnapshot = await getDocs(existingQuery);
    
    if (!existingSnapshot.empty) {
      throw new Error('Word is already banned');
    }

    // Add to banned words collection
    await addDoc(collection(db, 'banned_words'), {
      word: normalizedWord,
      reason: reason || '',
      bannedBy: currentUser.uid,
      bannedAt: new Date(),
    });

    console.log(`Successfully banned word: "${normalizedWord}"`);
  } catch (error) {
    console.error('Error banning word:', error);
    throw error;
  }
}

// Remove a word from banned list
export async function unbanWord(bannedWordId: string): Promise<void> {
  try {
    // Check admin status
    const isAdmin = await checkAdminStatus();
    if (!isAdmin) {
      throw new Error('You must be an admin to unban words');
    }

    // Delete from banned words collection
    const bannedWordRef = doc(db, 'banned_words', bannedWordId);
    await deleteDoc(bannedWordRef);

    console.log(`Successfully unbanned word with ID: "${bannedWordId}"`);
  } catch (error) {
    console.error('Error unbanning word:', error);
    throw error;
  }
}

// Check if a word is banned
export async function isWordBanned(word: string): Promise<boolean> {
  try {
    const normalizedWord = word.toLowerCase().trim();
    
    const bannedQuery = query(
      collection(db, 'banned_words'),
      where('word', '==', normalizedWord)
    );
    
    const snapshot = await getDocs(bannedQuery);
    return !snapshot.empty;
  } catch (error) {
    console.error('Error checking if word is banned:', error);
    return false;
  }
}

// Format banned words for AI prompt
export function formatBannedWordsForPrompt(bannedWords: string[]): string {
  if (bannedWords.length === 0) {
    return '';
  }
  
  return `\n\nIMPORTANT: Do NOT include any of these banned words in your response: ${bannedWords.join(', ')}. If your natural response would include any of these words, please choose a different but equally creative alternative.`;
}
